
import { getCachedItem, setCachedItem } from '@/utils/clientCacheUtils';
import { CACHE_STATS_STORE } from './cache-constants';

// Enhanced cache metrics tracking
export interface CacheMetrics {
  hits: number;
  misses: number;
  sets: number;
  lastUpdated: Date;
  hitRate?: number;  // Add hitRate property
  byLanguage?: Record<string, { hits: number, misses: number }>;
  byTopic?: Record<string, { hits: number, misses: number }>;
  sizeMetrics?: {
    totalSize: number;
    itemCount: number;
    avgCompressionRatio?: number;
  };
}

// Update cache metrics with enhanced tracking
export const updateCacheMetrics = async (
  storeName: string,
  action: 'hit' | 'miss' | 'set',
  metadata: Record<string, any> = {}
): Promise<void> => {
  try {
    // Get current metrics
    let metrics = await getCachedItem<CacheMetrics>(`metrics_${storeName}`, CACHE_STATS_STORE);
    
    // Initialize metrics if they don't exist
    if (!metrics) {
      metrics = {
        hits: 0,
        misses: 0,
        sets: 0,
        lastUpdated: new Date(),
        byLanguage: {},
        byTopic: {},
        sizeMetrics: {
          totalSize: 0,
          itemCount: 0
        }
      };
    }
    
    // Ensure sub-objects exist
    metrics.byLanguage = metrics.byLanguage || {};
    metrics.byTopic = metrics.byTopic || {};
    metrics.sizeMetrics = metrics.sizeMetrics || { totalSize: 0, itemCount: 0 };
    
    // Update language-specific metrics if language is provided
    if (metadata.language) {
      const language = metadata.language;
      metrics.byLanguage[language] = metrics.byLanguage[language] || { hits: 0, misses: 0 };
      
      if (action === 'hit') {
        metrics.byLanguage[language].hits = (metrics.byLanguage[language].hits || 0) + 1;
      } else if (action === 'miss') {
        metrics.byLanguage[language].misses = (metrics.byLanguage[language].misses || 0) + 1;
      }
    }
    
    // Update topic-specific metrics if topic is provided
    if (metadata.topic) {
      const topic = metadata.topic;
      metrics.byTopic[topic] = metrics.byTopic[topic] || { hits: 0, misses: 0 };
      
      if (action === 'hit') {
        metrics.byTopic[topic].hits = (metrics.byTopic[topic].hits || 0) + 1;
      } else if (action === 'miss') {
        metrics.byTopic[topic].misses = (metrics.byTopic[topic].misses || 0) + 1;
      }
    }
    
    // Update size metrics if available
    if (action === 'set') {
      // Update item count
      metrics.sizeMetrics.itemCount = (metrics.sizeMetrics.itemCount || 0) + 1;
      
      // Update total size if provided
      if (metadata.totalSize) {
        metrics.sizeMetrics.totalSize = (metrics.sizeMetrics.totalSize || 0) + metadata.totalSize;
      }
      
      // Update compression metrics for embeddings
      if (metadata.compressionRatio) {
        const currentRatio = metrics.sizeMetrics.avgCompressionRatio || 0;
        const currentCount = metrics.sets || 0;
        
        // Calculate new average compression ratio
        metrics.sizeMetrics.avgCompressionRatio = 
          (currentRatio * currentCount + metadata.compressionRatio) / (currentCount + 1);
      }
    }
    
    // Update the appropriate counter
    switch (action) {
      case 'hit':
        metrics.hits++;
        break;
      case 'miss':
        metrics.misses++;
        break;
      case 'set':
        metrics.sets++;
        break;
    }
    
    metrics.lastUpdated = new Date();
    
    // Save updated metrics
    await setCachedItem(
      `metrics_${storeName}`,
      metrics,
      CACHE_STATS_STORE,
      { maxAge: 30 * 24 * 60 * 60 * 1000 } // 30 days
    );
  } catch (error) {
    console.error('Error updating cache metrics:', error);
  }
};

// Get cache metrics for a store with enhanced information
export const getCacheMetrics = async (
  storeName: string
): Promise<CacheMetrics | null> => {
  try {
    return await getCachedItem<CacheMetrics>(`metrics_${storeName}`, CACHE_STATS_STORE);
  } catch (error) {
    console.error('Error getting cache metrics:', error);
    return null;
  }
};

// Get hit rate for a store
export const getCacheHitRate = async (
  storeName: string
): Promise<number | null> => {
  try {
    const metrics = await getCacheMetrics(storeName);
    if (!metrics) return null;
    
    const total = metrics.hits + metrics.misses;
    if (total === 0) return 0;
    
    return metrics.hits / total;
  } catch (error) {
    console.error('Error calculating hit rate:', error);
    return null;
  }
};

// Get metrics for all stores with enhanced detail
export const getAllCacheMetrics = async (): Promise<Record<string, CacheMetrics>> => {
  try {
    const results: Record<string, CacheMetrics> = {};
    const stores = ['embeddings', 'responses', 'messages'];
    
    for (const store of stores) {
      const metrics = await getCacheMetrics(store);
      if (metrics) {
        results[store] = metrics;
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error getting all cache metrics:', error);
    return {};
  }
};

// Get cache size metrics for visualization
export const getCacheSizeMetrics = async (): Promise<Record<string, number>> => {
  try {
    const metrics = await getAllCacheMetrics();
    const sizeMetrics: Record<string, number> = {};
    
    for (const [store, storeMetrics] of Object.entries(metrics)) {
      sizeMetrics[store] = storeMetrics.sizeMetrics?.totalSize || 0;
    }
    
    return sizeMetrics;
  } catch (error) {
    console.error('Error getting cache size metrics:', error);
    return {};
  }
};
